import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { Row, Col, Form } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import DateRangeFilter from "../components/DateRangeFilter";
import useFetchData from "../components/useFetchDatav2";
import SIDES, { renderCell } from "./invoicesSides";
import StatusPill from "../components/invoices/StatusPill";
import SummaryStrip from "../components/invoices/SummaryStrip";
import SideToggle from "../components/invoices/SideToggle";
import ErpDetailPanel from "../components/invoices/ErpDetailPanel";

// current + prior calendar month, anchored on invoice date
const defaultRange = () => {
   const now = new Date();
   return {
      startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0),
      endDate: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59),
      key: "selection",
   };
};

const STATUS_OPTIONS = [
   { value: "attention", label: "Needs attention" },
   { value: "unmatched", label: "Unmatched" },
   { value: "partial", label: "Partial" },
   { value: "matched", label: "Matched" },
   { value: "all", label: "All" },
];

// Fixed column widths (table-layout: fixed) so switching sides/filters doesn't reflow the
// table. Text columns (supplier, invoice no) get no width and share the remaining space.
const COLW = { status: "84px", money: "120px", date: "120px", text: undefined };

export default function Invoices() {
   const navigate = useNavigate();
   const { fetchData } = useFetchData();

   const [side, setSide] = useState("statement");
   const [range, setRange] = useState(defaultRange());
   const [status, setStatus] = useState("attention");
   const [invoiceInput, setInvoiceInput] = useState(""); // controlled text box (responsive)
   const [invoiceNo, setInvoiceNo] = useState(""); // debounced query value used in load/buildBody
   const [pageIndex, setPageIndex] = useState(0);
   const [pageSize] = useState(20);

   const [rows, setRows] = useState([]);
   const [counts, setCounts] = useState(null);
   const [totalPages, setTotalPages] = useState(1);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState(null);
   const [erpRow, setErpRow] = useState(null);

   const cfg = SIDES[side];

   // Hold the latest fetchData in a ref so it need not be a dependency of `load`.
   // useFetchDatav2 returns a NEW fetchData reference every render (it is not memoized),
   // so depending on it directly would give `load` a new identity on every commit and
   // make the `[load]` effect re-run on every render (refetch storm).
   const fetchDataRef = useRef(fetchData);
   useEffect(() => {
      fetchDataRef.current = fetchData; // keep latest each render
   });

   const load = useCallback(async () => {
      setLoading(true);
      setError(null);
      const body = cfg.buildBody({
         from: range.startDate,
         to: range.endDate,
         status,
         sort: "attention",
         invoiceNo,
         pageIndex,
         pageSize,
      });
      const { responseData, fetchError } = await fetchDataRef.current(cfg.endpoint, "POST", body, "form-data");
      setLoading(false);
      if (fetchError || !responseData || responseData.st !== 1) {
         setError(responseData?.mg || "Unable to load invoices.");
         setRows([]);
         setCounts(null);
         setTotalPages(1);
         return;
      }
      setRows(Array.isArray(responseData.payload) ? responseData.payload : []);
      setCounts(responseData.counts || null);
      setTotalPages(responseData.total_pages || 1);
   }, [cfg, range, status, invoiceNo, pageIndex, pageSize]);

   useEffect(() => {
      load();
   }, [load]);

   // reset to first page whenever a filter or side changes
   useEffect(() => {
      setPageIndex(0);
   }, [side, status, invoiceNo, range]);

   // Debounce the invoice-number search: the text box stays responsive via `invoiceInput`,
   // while the query value `invoiceNo` (used by load/buildBody) updates ~400ms after the
   // user pauses typing — avoids hammering the Postgres LIKE on every keystroke.
   const debouncedSetInvoiceNo = useMemo(() => debounce((v) => setInvoiceNo(v), 400), []);
   useEffect(() => {
      debouncedSetInvoiceNo(invoiceInput);
   }, [invoiceInput, debouncedSetInvoiceNo]);
   useEffect(() => () => debouncedSetInvoiceNo.cancel(), [debouncedSetInvoiceNo]);

   const goReconcile = (row) => {
      navigate("/apps/reconcile", {
         state: {
            selectedInvoiceStatement: row.sup_state_external_id,
            selectedInvoiceStatementSupplierName: row.supplier,
            selectedInvoiceStatementDate: row.statement_date,
         },
      });
   };

   const handleRowClick = (row) => {
      if (side === "statement") {
         if (row.sup_state_external_id) goReconcile(row);
         return;
      }
      // ERP side: matched/partial → jump to its statement; unmatched → detail panel
      if (row.sup_state_external_id) goReconcile(row);
      else setErpRow(row);
   };

   return (
      <>
         <Header />
         <div className="main main-app p-3 p-lg-4 p-xxl-5">
            <Row className="g-3 g-lg-4">
               <Col>
                  <h2 className="main-title fs-4 mb-1">Invoices</h2>
                  <p className="text-secondary mb-3">Statement and ERP invoices in one view — unmatched and partial first.</p>
               </Col>
            </Row>

            <Row className="align-items-end g-3 mb-2">
               <Col xs="auto">
                  <SideToggle side={side} onChange={setSide} />
               </Col>
               <Col xs="auto">
                  <Form.Label className="d-block mb-1">Status</Form.Label>
                  <Form.Select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 180 }}>
                     {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                           {o.label}
                        </option>
                     ))}
                  </Form.Select>
               </Col>
               <Col xs="auto">
                  <Form.Label className="d-block mb-1">Invoice date</Form.Label>
                  <DateRangeFilter
                     column={{ id: "inv_date", value: range }}
                     handleInputFilterChangeNoDebounce={(_id, value) => setRange(value)}
                  />
               </Col>
               <Col xs="auto">
                  <Form.Label className="d-block mb-1">Invoice number</Form.Label>
                  <Form.Control
                     value={invoiceInput}
                     placeholder="Search invoice no."
                     onChange={(e) => setInvoiceInput(e.target.value)}
                     style={{ width: 200 }}
                  />
               </Col>
            </Row>

            <SummaryStrip counts={counts} />

            {error && <div className="alert alert-danger">{error}</div>}

            <Row className="g-3 g-lg-4">
               <Col>
                  <div className="data-table-card">
                     <table className="table" style={{ tableLayout: "fixed", width: "100%" }}>
                        <thead>
                           <tr>
                              {cfg.columns.map((c) => (
                                 <th key={c.key} style={{ width: COLW[c.kind] }}>
                                    {c.label}
                                 </th>
                              ))}
                           </tr>
                        </thead>
                        {/* Keep prior rows visible (dimmed) during a refetch so toggling sides/filters
                            doesn't collapse the table height and shift the page (scrollbar) — only show
                            the single-line message on a genuinely empty result. */}
                        <tbody style={{ opacity: loading && rows.length ? 0.5 : 1, transition: "opacity 0.12s ease" }}>
                           {rows.length === 0 ? (
                              <tr>
                                 <td colSpan={cfg.columns.length} className="text-secondary">
                                    {loading ? "Loading…" : "No invoices match your filters."}
                                 </td>
                              </tr>
                           ) : (
                              rows.map((row) => (
                                 <tr key={cfg.rowKey(row)} style={{ cursor: "pointer" }} onClick={() => handleRowClick(row)}>
                                    {cfg.columns.map((c) => (
                                       <td
                                          key={c.key}
                                          style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                          {c.kind === "status" ? <StatusPill status={row[c.key]} /> : renderCell(c.kind, row[c.key])}
                                       </td>
                                    ))}
                                 </tr>
                              ))
                           )}
                        </tbody>
                     </table>
                  </div>
               </Col>
            </Row>

            {rows.length > 0 && (
               <Row className="g-3 g-lg-4">
                  <Col>
                     <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex justify-content-center flex-grow-1">
                           <span>
                              Page <strong>{pageIndex + 1}</strong> of {totalPages || 1}
                           </span>
                        </div>
                        <div className="d-flex justify-content-end">
                           <button
                              className="btn btn-primary me-2"
                              onClick={() => setPageIndex((p) => Math.max(p - 1, 0))}
                              disabled={pageIndex === 0}>
                              Previous
                           </button>
                           <button
                              className="btn btn-primary"
                              onClick={() => setPageIndex((p) => Math.min(p + 1, totalPages - 1))}
                              disabled={pageIndex >= totalPages - 1}>
                              Next
                           </button>
                        </div>
                     </div>
                  </Col>
               </Row>
            )}

            <ErpDetailPanel row={erpRow} onClose={() => setErpRow(null)} />
            <Footer />
         </div>
      </>
   );
}
